import React, { useState, useRef, useEffect } from 'react';
import { X, FileText, Sheet, Loader, ChevronDown } from 'lucide-react';
import documentsApi from '../../../api/documentsApi';
import { getTagPillClasses, isSameTagLabel, normalizeTagLabel } from '../utils/tagStyles';
import type { UploadDocumentResponse } from '../../../api/types';

export interface FileWithMeta {
    file: File;
    documentType: string;
    tags: string[];
}

interface DocumentsMultiUploadModalProps {
    files: File[];
    onClose: () => void;
    onSaveComplete: (results: UploadDocumentResponse[]) => void;
    businessId?: string;
    availableTagLabels?: string[];
    onLabelsChanged?: () => void;
}

type DropdownPosition = {
    top?: number;
    bottom?: number;
    left: number;
    width: number;
};

const DOCUMENT_TYPES = [
    { value: 'invoice', label: 'Invoice' },
    { value: 'expenses', label: 'Expense' },
    { value: 'bills', label: 'Bill' },
    { value: 'other', label: 'Other' },
];

const getFileIconConfig = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (['csv', 'xls', 'xlsx'].includes(ext || '')) {
        return { Icon: Sheet, color: 'text-green-600', bg: 'bg-green-50' };
    }
    return { Icon: FileText, color: 'text-red-600', bg: 'bg-red-50' };
};

const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(0)) + ' ' + sizes[i];
};

const DocumentsMultiUploadModal: React.FC<DocumentsMultiUploadModalProps> = ({
    files,
    onClose,
    onSaveComplete,
    businessId,
    availableTagLabels = [],
    onLabelsChanged,
}) => {
    const [items, setItems] = useState<FileWithMeta[]>(() =>
        files.map((f) => ({ file: f, documentType: '', tags: [] }))
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number; current?: string }>({ done: 0, total: files.length });
    const [showTypeDropdownFor, setShowTypeDropdownFor] = useState<number | null>(null);
    const [typeDropdownPosition, setTypeDropdownPosition] = useState<DropdownPosition | null>(null);
    const [tagInputFor, setTagInputFor] = useState<number | null>(null);
    const [tagInputValue, setTagInputValue] = useState('');
    const [deletingLabel, setDeletingLabel] = useState<string | null>(null);
    const typeDropdownRefs = useRef<(HTMLDivElement | null)[]>([]);
    const tagInputRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (!typeDropdownRefs.current.some((r) => r?.contains(target))) {
                setShowTypeDropdownFor(null);
            }
            if (!tagInputRefs.current.some((r) => r?.contains(target))) {
                setTagInputFor(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (showTypeDropdownFor === null) {
            return;
        }

        const updateTypeDropdownPosition = () => {
            const trigger = typeDropdownRefs.current[showTypeDropdownFor];
            if (!trigger) {
                return;
            }

            const rect = trigger.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const estimatedHeight = Math.min(DOCUMENT_TYPES.length * 40 + 8, 240);
            const spaceBelow = viewportHeight - rect.bottom;
            const openUpward = spaceBelow < estimatedHeight && rect.top > spaceBelow;

            setTypeDropdownPosition({
                left: rect.left,
                width: rect.width,
                top: openUpward ? undefined : rect.bottom + 4,
                bottom: openUpward ? Math.max(viewportHeight - rect.top + 4, 8) : undefined,
            });
        };

        updateTypeDropdownPosition();
        window.addEventListener('resize', updateTypeDropdownPosition);
        window.addEventListener('scroll', updateTypeDropdownPosition, true);

        return () => {
            window.removeEventListener('resize', updateTypeDropdownPosition);
            window.removeEventListener('scroll', updateTypeDropdownPosition, true);
        };
    }, [showTypeDropdownFor]);

    const updateItem = (index: number, updates: Partial<FileWithMeta>) => {
        setItems((prev) => prev.map((it, i) => (i === index ? { ...it, ...updates } : it)));
    };

    const applyTypeToAll = (type: string) => {
        setItems((prev) => prev.map((it) => ({ ...it, documentType: type })));
    };

    const applyTagsToAll = (tags: string[]) => {
        setItems((prev) => prev.map((it) => ({ ...it, tags: [...tags] })));
    };

    const handleAddTag = async (index: number, tag: string) => {
        const t = normalizeTagLabel(tag);
        if (!t) return;
        const item = items[index];
        if (item.tags.some((current) => isSameTagLabel(current, t))) return;
        updateItem(index, { tags: [...item.tags, t] });
        try {
            await documentsApi.addTagLabel(t, businessId);
            onLabelsChanged?.();
        } catch (err) {
            console.error('Failed to add tag label:', err);
        }
        setTagInputValue('');
    };

    const handleRemoveTag = (index: number, tag: string) => {
        updateItem(index, { tags: items[index].tags.filter((t) => !isSameTagLabel(t, tag)) });
    };

    const handleDeleteLabel = async (label: string) => {
        if (deletingLabel) return;
        setDeletingLabel(label);
        try {
            await documentsApi.deleteTagLabel(label, businessId);
            setItems((prev) => prev.map((item) => ({
                ...item,
                tags: item.tags.filter((tag) => !isSameTagLabel(tag, label)),
            })));
            onLabelsChanged?.();
        } catch (err: any) {
            alert(err?.message || 'Failed to delete label.');
        } finally {
            setDeletingLabel(null);
        }
    };

    const handleSave = async () => {
        const invalid = items.find((it) => !it.documentType);
        if (invalid) {
            setError('Please select a document type for all files.');
            return;
        }

        setLoading(true);
        setError(null);

        let done = 0;
        const total = items.length;
        const results: UploadDocumentResponse[] = [];

        try {
            for (let i = 0; i < items.length; i++) {
                const { file, documentType, tags } = items[i];
                setUploadProgress({ done, total, current: file.name });

                const response = await documentsApi.uploadDocument(file, documentType, tags, businessId);
                results.push(response);

                done += 1;
                setUploadProgress({ done, total, current: undefined });
            }

            onSaveComplete(results);
        } catch (err: any) {
            console.error(err);
            setError(err.message || 'Failed to upload one or more documents.');
        } finally {
            setLoading(false);
        }
    };

    const canSave = items.every((it) => it.documentType) && !loading;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-6">
            <div
                className="bg-white relative flex flex-col shadow-2xl"
                style={{
                    width: '100%',
                    maxWidth: '800px',
                    maxHeight: '90vh',
                    borderRadius: '12px',
                    padding: '32px',
                }}
            >
                <button
                    onClick={onClose}
                    className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <X className="w-6 h-6" />
                </button>

                <h2 className="text-[20px] font-semibold text-gray-900 mb-2">
                    Upload Documents ({files.length} {files.length === 1 ? 'file' : 'files'})
                </h2>
                <p className="text-sm text-gray-500 mb-6">
                    Assign document type and tags to each file. Use &quot;Apply to all&quot; for batch assignment.
                </p>

                {error && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>
                )}

                {/* Batch actions */}
                <div className="flex gap-2 mb-4 flex-wrap items-center">
                    <select
                        onChange={(e) => {
                            const v = e.target.value;
                            if (v) applyTypeToAll(v);
                        }}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 py-1.5 px-3 rounded-lg text-sm border border-gray-200"
                    >
                        <option value="">Apply type to all</option>
                        {DOCUMENT_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>
                                {t.label}
                            </option>
                        ))}
                    </select>
                    <span className="text-gray-400 text-sm self-center">or</span>
                    <div className="flex flex-wrap gap-1 items-center">
                        <span className="text-sm text-gray-600">Apply tags:</span>
                        {availableTagLabels.slice(0, 4).map((tag) => (
                            <button
                                key={tag}
                                type="button"
                                onClick={() => applyTagsToAll([tag])}
                                className={`px-2 py-0.5 rounded text-xs border ${getTagPillClasses(tag)}`}
                            >
                                +{tag}
                            </button>
                        ))}
                    </div>
                </div>

                {availableTagLabels.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                        {availableTagLabels.map((label) => (
                            <span
                                key={`saved-${label}`}
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs ${getTagPillClasses(label)}`}
                            >
                                {label}
                                <button
                                    type="button"
                                    disabled={deletingLabel === label}
                                    onClick={() => void handleDeleteLabel(label)}
                                    className="hover:opacity-80 disabled:opacity-50"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                {/* File list */}
                <div className="overflow-y-auto flex-1 min-h-0 mb-6 max-h-[320px] space-y-3">
                    {items.map((item, index) => {
                        const { Icon, color, bg } = getFileIconConfig(item.file.name);
                        return (
                            <div
                                key={index}
                                className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm"
                            >
                                <div className="flex items-start gap-4">
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${bg}`}>
                                        <Icon className={`w-6 h-6 ${color}`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">{item.file.name}</p>
                                        <p className="text-xs text-gray-500">{formatSize(item.file.size)}</p>

                                        <div className="mt-3">
                                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                                Document Type <span className="text-red-500">*</span>
                                            </label>
                                            <div
                                                ref={(el) => { typeDropdownRefs.current[index] = el; }}
                                                className="relative"
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setShowTypeDropdownFor(showTypeDropdownFor === index ? null : index);
                                                    }}
                                                    className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-left text-sm"
                                                >
                                                    <span className={item.documentType ? 'text-gray-900' : 'text-gray-400'}>
                                                        {item.documentType
                                                            ? DOCUMENT_TYPES.find((t) => t.value === item.documentType)?.label
                                                            : 'Select type...'}
                                                    </span>
                                                    <ChevronDown className="w-4 h-4 text-gray-400" />
                                                </button>
                                                {showTypeDropdownFor === index && (
                                                    <div
                                                        className="fixed bg-white border rounded-lg shadow-lg z-[9999] py-1 overflow-y-auto max-h-60"
                                                        style={typeDropdownPosition ?? undefined}
                                                    >
                                                        {DOCUMENT_TYPES.map((t) => (
                                                            <button
                                                                key={t.value}
                                                                type="button"
                                                                onClick={() => {
                                                                    updateItem(index, { documentType: t.value });
                                                                    setShowTypeDropdownFor(null);
                                                                }}
                                                                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${item.documentType === t.value ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
                                                            >
                                                                {t.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div ref={(el) => { tagInputRefs.current[index] = el; }} className="mt-2">
                                            <label className="block text-xs font-medium text-gray-600 mb-1">Tags</label>
                                            <div className="flex flex-wrap gap-1 mb-1">
                                                {item.tags.map((tag) => (
                                                    <span
                                                        key={tag}
                                                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${getTagPillClasses(tag)}`}
                                                    >
                                                        {tag}
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveTag(index, tag)}
                                                            className="hover:text-blue-900"
                                                        >
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                            <input
                                                type="text"
                                                value={tagInputFor === index ? tagInputValue : ''}
                                                onChange={(e) => setTagInputValue(e.target.value)}
                                                onFocus={() => setTagInputFor(index)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ',') {
                                                        e.preventDefault();
                                                        void handleAddTag(index, tagInputValue);
                                                    }
                                                }}
                                                placeholder="Add tag, press Enter"
                                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                            />
                                            {tagInputFor === index && (
                                                <div className="mt-1 flex flex-wrap gap-1">
                                                    {availableTagLabels.filter((t) => !item.tags.some((tag) => isSameTagLabel(tag, t)))
                                                        .slice(0, 6)
                                                        .map((t) => (
                                                            <button
                                                                key={t}
                                                                type="button"
                                                                onClick={() => void handleAddTag(index, t)}
                                                                className={`px-2 py-0.5 rounded text-xs border ${getTagPillClasses(t)}`}
                                                            >
                                                                {t}
                                                            </button>
                                                        ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {loading && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                        <div className="flex items-center gap-2">
                            <Loader className="w-4 h-4 animate-spin text-blue-600" />
                            <span className="text-sm text-blue-900">
                                Uploading {uploadProgress.done}/{uploadProgress.total}
                                {uploadProgress.current && ` — ${uploadProgress.current}`}
                            </span>
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-3 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 py-2.5 px-6 rounded-lg text-sm font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={!canSave}
                        className={`font-medium py-2.5 px-8 rounded-lg text-sm flex items-center gap-2 ${
                            canSave ? 'bg-gray-900 text-white hover:bg-gray-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        {loading && <Loader className="w-4 h-4 animate-spin" />}
                        Upload {files.length} {files.length === 1 ? 'file' : 'files'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DocumentsMultiUploadModal;
